export const CREATOR_SITE_LEAD_SOURCE = 'creator_site';
export const CREATOR_SITE_LEAD_TAG = 'site-subscriber';
export const CREATOR_SITE_INQUIRY_TAG = 'site-inquiry';
export const CREATOR_SITE_LEAD_EMAIL_MAX = 320;
export const CREATOR_SITE_LEAD_NAME_MAX = 120;
export const CREATOR_SITE_LEAD_MESSAGE_MAX = 5000;
export const CREATOR_SITE_INQUIRY_TYPES_MAX = 8;
export const CREATOR_SITE_LEAD_CAPTURE_TYPES = ['newsletter', 'inquiry'] as const;
export type CreatorSiteLeadCaptureType = (typeof CREATOR_SITE_LEAD_CAPTURE_TYPES)[number];
export const CREATOR_SITE_NEWSLETTER_DEFAULT_HEADLINE = 'Join the newsletter';
export const CREATOR_SITE_NEWSLETTER_DEFAULT_BUTTON_LABEL = 'Subscribe';
export const CREATOR_SITE_NEWSLETTER_DEFAULT_CONSENT_TEXT = 'Get updates from this creator.';
export const CREATOR_SITE_CONTACT_DEFAULT_HEADLINE = 'Get in touch';
export const CREATOR_SITE_CONTACT_DEFAULT_BUTTON_LABEL = 'Send message';
export const CREATOR_SITE_CONTACT_DEFAULT_CONSENT_TEXT =
  'You agree to be contacted about this inquiry.';

export type CreatorSiteNewsletterLeadInput = {
  email: string;
  sectionId: string;
  consentAccepted: boolean;
  consentTextVersion: string;
  source: string;
  honeypot?: string | null;
};

export type CreatorSiteContactLeadInput = {
  name: string;
  email: string;
  message: string;
  sectionId: string;
  consentAccepted: boolean;
  consentTextVersion: string;
  source: string;
  inquiryType?: string | null;
  honeypot?: string | null;
};

/** @deprecated Use CreatorSiteNewsletterLeadInput */
export type CreatorSiteLeadInput = CreatorSiteNewsletterLeadInput;

type ValidationResult = { ok: true } | { ok: false; message: string };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function creatorSiteNewsletterConsentText(content: Record<string, unknown> | null | undefined) {
  const raw = content?.consentText;
  return typeof raw === 'string' && raw.trim()
    ? raw.trim()
    : CREATOR_SITE_NEWSLETTER_DEFAULT_CONSENT_TEXT;
}

export function creatorSiteContactConsentText(content: Record<string, unknown> | null | undefined) {
  const raw = content?.consentText;
  return typeof raw === 'string' && raw.trim()
    ? raw.trim()
    : CREATOR_SITE_CONTACT_DEFAULT_CONSENT_TEXT;
}

export function creatorSiteNewsletterConsentVersion(consentText: string) {
  const normalized = consentText.trim();
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = ((hash << 5) + hash) ^ normalized.charCodeAt(i);
  }
  return `v1-${(hash >>> 0).toString(16)}`;
}

export function validateCreatorSiteNewsletterLeadInput(
  input: CreatorSiteNewsletterLeadInput,
): ValidationResult {
  const email = input.email?.trim() ?? '';
  if (!email || email.length > CREATOR_SITE_LEAD_EMAIL_MAX || !EMAIL_PATTERN.test(email)) {
    return { ok: false, message: 'A valid email address is required' };
  }

  const sectionId = input.sectionId?.trim() ?? '';
  if (!sectionId) {
    return { ok: false, message: 'sectionId is required' };
  }

  if (!input.consentAccepted) {
    return { ok: false, message: 'Consent is required' };
  }

  const consentTextVersion = input.consentTextVersion?.trim() ?? '';
  if (!consentTextVersion) {
    return { ok: false, message: 'consentTextVersion is required' };
  }

  if (input.source !== CREATOR_SITE_LEAD_SOURCE) {
    return { ok: false, message: 'Invalid lead source' };
  }

  return { ok: true };
}

export function validateCreatorSiteContactLeadInput(
  input: CreatorSiteContactLeadInput,
  options?: { allowedInquiryTypes?: string[] },
): ValidationResult {
  const name = input.name?.trim() ?? '';
  if (!name || name.length > CREATOR_SITE_LEAD_NAME_MAX) {
    return { ok: false, message: 'A valid name is required' };
  }

  const email = input.email?.trim() ?? '';
  if (!email || email.length > CREATOR_SITE_LEAD_EMAIL_MAX || !EMAIL_PATTERN.test(email)) {
    return { ok: false, message: 'A valid email address is required' };
  }

  const message = input.message?.trim() ?? '';
  if (!message || message.length > CREATOR_SITE_LEAD_MESSAGE_MAX) {
    return { ok: false, message: 'A message is required' };
  }

  const sectionId = input.sectionId?.trim() ?? '';
  if (!sectionId) {
    return { ok: false, message: 'sectionId is required' };
  }

  if (!input.consentAccepted) {
    return { ok: false, message: 'Consent is required' };
  }

  const consentTextVersion = input.consentTextVersion?.trim() ?? '';
  if (!consentTextVersion) {
    return { ok: false, message: 'consentTextVersion is required' };
  }

  if (input.source !== CREATOR_SITE_LEAD_SOURCE) {
    return { ok: false, message: 'Invalid lead source' };
  }

  const inquiryType = input.inquiryType?.trim() ?? '';
  const allowedTypes = options?.allowedInquiryTypes ?? [];
  if (allowedTypes.length > 0) {
    if (!inquiryType) {
      return { ok: false, message: 'inquiryType is required' };
    }
    if (!allowedTypes.includes(inquiryType)) {
      return { ok: false, message: 'Invalid inquiry type' };
    }
  } else if (inquiryType) {
    return { ok: false, message: 'inquiryType is not supported for this form' };
  }

  return { ok: true };
}

/** @deprecated Use validateCreatorSiteNewsletterLeadInput */
export function validateCreatorSiteLeadInput(input: CreatorSiteNewsletterLeadInput): ValidationResult {
  return validateCreatorSiteNewsletterLeadInput(input);
}
